/**
 * @brisa/ui · facelift/event-card/EventCardHorizontal
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #17 (event-card-horizontal)
 * Figma origem: node 164:2810 — reforma Bruno 2026-05-14:
 *   - bg #7E8D79 (sage solid, NÃO dark scrim)
 *   - rounded-full (pill, não xl)
 *   - padding 10px (não 16)
 *   - title 1.2rem text-black (não 1.5rem branco)
 *   - subtitle text-black/56
 *   - data formatada "DD de mês" (não DD/MM) — locale pt
 *   - hora text-black/40
 *   - avatar 56x56 (size lg, antes era 48)
 *
 * Sem 'use client' — apresentacional puro.
 */
import * as React from 'react';

import { AvatarClube } from '../avatar-clube/AvatarClube';
import { cn } from '@brisa/ui/lib/utils';

export interface EventCardEvent {
  id: string;
  title: string;
  subtitle?: string;
  date: string; // ISO date
  time: string;
  club: {
    name: string;
    avatar_url?: string | null;
    slug?: string;
  };
}

export interface EventCardHorizontalProps extends React.HTMLAttributes<HTMLDivElement> {
  event: EventCardEvent;
  /** locale para formatação da data; default 'pt-BR' (DD de mês). */
  locale?: 'pt-BR' | 'es-ES';
}

const MONTHS_PT_BR = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const MONTHS_ES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

function formatLongDate(iso: string, locale: 'pt-BR' | 'es-ES'): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.getUTCDate();
  const month = (locale === 'es-ES' ? MONTHS_ES_ES : MONTHS_PT_BR)[date.getUTCMonth()];
  const connector = locale === 'es-ES' ? 'de' : 'de';
  return `${day} ${connector} ${month}`;
}

export function EventCardHorizontal({
  event,
  locale = 'pt-BR',
  className,
  ...props
}: EventCardHorizontalProps) {
  return (
    <article
      className={cn(
        'flex items-center gap-4 rounded-full bg-[#7E8D79] p-[10px] text-[hsl(var(--text-100))]',
        className,
      )}
      data-slot="event-card"
      {...props}
    >
      <AvatarClube club={event.club} size="lg" withShadow={false} />

      <div className="flex min-w-0 flex-1 flex-col gap-1" data-slot="body">
        <h3
          className="truncate font-body text-[1.2rem] font-medium leading-tight text-[hsl(var(--text-100))]"
          data-slot="title"
        >
          {event.title}
        </h3>
        {event.subtitle ? (
          <p
            className="truncate text-sm leading-tight text-[hsl(var(--text-56))]"
            data-slot="subtitle"
          >
            {event.subtitle}
          </p>
        ) : null}
      </div>

      <div
        className="flex shrink-0 flex-col items-end pr-4 text-right text-sm leading-tight"
        data-slot="datetime"
      >
        <span className="font-medium text-[hsl(var(--text-100))]" data-slot="date">
          {formatLongDate(event.date, locale)}
        </span>
        <span className="text-[hsl(var(--text-40))]" data-slot="time">
          {event.time}
        </span>
      </div>
    </article>
  );
}
